import React, { useEffect, useState } from 'react';
import { Chart, ChartOption } from './Chart';
import { Box } from '@mui/material';
import CurrencyFormat from '@/utils/CurrencyFormat';
import { formatLargeNumber } from '@/utils/formatLargNumber';
import PeriodFilter from './PeriodFilter';
import { groupBy } from 'lodash';
import { generateLastYears } from '@/features/dashboard/CashFlowTela';

interface MonthData {
  nome: string;
  valor: number;
  valor_secundario: number;
  valor_terciario: number;
}

interface objResponse {
  order: string,
  entry_value: number,
  exit_value: number,
  invoicing_value: number
}

interface ChartCashFlowProps {
  title: string;
  data: objResponse[];
  showFilter?: boolean;
  setFilter?: (filter: any) => void;
  filter?: any;
}

const ChartCashFlow: React.FC<ChartCashFlowProps> = ({ title, data, showFilter, setFilter, filter }) => {
  const months = data?.map(item => item.order);
  const incomeData = data?.map(item => -item.exit_value);
  const secondaryData = data?.map(item => item.entry_value);
  const tertiaryData = data?.map(item => item.entry_value - item.exit_value);

  const [listYears, setListYears] = useState(generateLastYears(5));

  const [year, setYear] = useState<number | null>(null);

  useEffect(() => {
    if (data && listYears.length) {
      setYear(listYears[0]);
    }
  }, [data, listYears]);

  const onApplyFilter = (selectedYear) => {
    console.log('selectedYear', selectedYear);
    setFilter({year: selectedYear});
  };

  

  const option: ChartOption = {
    title: {
      text: title,
      padding: [
        16,
        5,
        5,
        23,
      ],
      textStyle: {
        fontSize: '14px'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params) {
        return params
          .filter(param => param.seriesName !== 'Income Base' && param.seriesName !== 'Expenses Base')
          .map((param) => {
            return `${param.seriesName}: ${CurrencyFormat.formatNumber(Number(param.value))}`;
          })
          .join('<br/>');
      },
    },
    legend: {
      data: [
        { name: 'Saídas', icon: 'circle' },
        { name: 'Entradas', icon: 'circle' },
        { name: 'Saldo', icon: 'circle' },
      ],
      right: '0',
      padding: [
        16,
        23,
        5,
        5,
      ],
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      top: '20%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: months,
    },
    yAxis: {
      type: 'value',
    },
    series: [
      {
        name: 'Income Base',
        type: 'bar',
        stack: 'Income',
        itemStyle: {
          color: 'transparent',
        },
        data: new Array(months.length).fill(-100),
      },
      {
        name: 'Entradas',
        type: 'bar',
        stack: 'Expenses',
        label: {
          show: true,
          position: 'inside',
          color: '#fff',
          fontSize: '12px',
          formatter: (params) => `R$ ${formatLargeNumber(Number(params.value))}`,
        },
        itemStyle: {
          color: '#28C76F',
        },
        barWidth: '30%',
        data: secondaryData,
      },
      {
        name: 'Saídas',
        type: 'bar',
        stack: 'Income',
        itemStyle: {
          color: '#E04A4C',
        },
        label: {
          show: true,
          position: 'inside',
          color: '#fff',
          fontSize: '12px',
          formatter: (params) => `R$ ${formatLargeNumber(Number(params.value))}`,
        },
        barWidth: '30%',
        data: incomeData,
      },

      {
        name: 'Saldo',
        type: 'bar',
        stack: 'Saldo',
        label: {
          show: true,
          position: 'inside',
          color: '#fff',
          fontSize: '12px',
          formatter: (params) => `R$ ${formatLargeNumber(Number(params.value))}`,
        },
        itemStyle: {
          color: '#17A2B8',
        },
        barWidth: '30%',
        data: tertiaryData,
      },
    ],
  };

  return (
    <Box
      sx={{ boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}
      height={'400px'}
    >
      <Chart option={option} />
      {showFilter && <Box marginTop={'10px'}>
        <PeriodFilter year={year} years={listYears} onApplyFilter={onApplyFilter} />
      </Box>}
    </Box>
  );
};

export default ChartCashFlow;
